import React, { FC, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { push as Push } from 'redux-first-history';
import { map } from 'lodash-es';
import styled from 'styled-components';

import { Campaign, CampaignStatus, CampaignTriggerType, CampaignType } from '@dev/ee-api/ee-api-models';
import { convertToLocalTime, safelyEncodeBase64Url } from '@dev/ee-tools';
import {
    ContentLoader,
    CustomTableRowsProps,
    DotsIcon,
    palette,
    SimplePopover as Popover,
    TemplatePreview,
    Typography,
    MobileCardRowsProps,
    formatNumber,
    TableRowNameLink,
} from '@dev/ee-components';

import { Box, Grid } from '@material-ui/core';
import { getApiPath } from '@/services/Secuirty';
import { CampaignsPaths } from '@/services/dictionaries/Paths';
import useMobile from '@/services/hooks/useMobile';
import { ApplicationState } from '@/store';
import { PreviewCampaign } from '@/store/campaigns/actions';
import { RowProps } from '../../CampaignsInterface';
import { getStatusBadge } from '../../helperFunctions';
import { getDropdownTableOptions } from '../helperFunctions';
import { useRequestParams } from './useRequestParams';

const TableItemButtonsWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    padding-left: 25px;
    align-items: center;
`;

const CampaignNameWrapper = styled.div`
    .campaign-name {
        word-break: break-all;
        overflow: hidden;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        display: -webkit-box;
        max-height: 65px;
        margin-bottom: 16px;
    }
`;

const TargetWrapper = styled(Typography)`
    margin-bottom: 5px;
`;

interface CampaignNameInterface {
    campaign: Campaign;
    onNameClick: () => void;
}

const CampaignName: FC<CampaignNameInterface> = (props: CampaignNameInterface): JSX.Element => {
    const { t } = useTranslation();
    const { campaign } = props;
    const showScheduleFor =
        ![CampaignStatus.Completed, CampaignStatus.Cancelled].includes(campaign.Status) &&
        campaign?.TriggerOptions?.Type === CampaignTriggerType.FutureScheduled;

    const { text, date } = [
        {
            text: t('scheduledfor'),
            date: convertToLocalTime(campaign?.TriggerOptions?.DateToSendOn),
            available: showScheduleFor,
        },
        {
            text: t('created'),
            date: convertToLocalTime(campaign?.DateAdded),
            available: !showScheduleFor,
        },
    ].find((op) => op.available);

    return (
        <CampaignNameWrapper>
            <Typography variant="caption" color={palette.gray[7]} className="campaign-name">
                <TableRowNameLink onClick={props.onNameClick}>{campaign.Name}</TableRowNameLink>
            </Typography>
            <Grid container wrap="wrap" direction="column">
                <TargetWrapper variant="body1" bodyVariant="help">
                    {[...(campaign?.Targets?.ListNames || []), ...(campaign?.Targets?.SegmentNames || [])].join(', ')}
                </TargetWrapper>
                <Grid item>
                    <Grid container>
                        <Typography variant="body1" bodyVariant="help">
                            {`${text}:`}
                        </Typography>
                        <Box ml="5px" />
                        <Typography variant="caption"> {date}</Typography>
                    </Grid>
                </Grid>
            </Grid>
        </CampaignNameWrapper>
    );
};

const getSkeletonRows = (perPage: number, isMobile: boolean): RowProps[] | MobileCardRowsProps[] => {
    const rows: RowProps[] = [];
    let index;
    for (index = 0; index < perPage; index++) {
        rows.push({
            preview: <ContentLoader mode="row" height={70} width={70} />,
            campaignName: (
                <>
                    <div style={{ marginBottom: '5px' }}>
                        <ContentLoader mode="row" height={15} />
                    </div>
                    <ContentLoader mode="row" height={15} />
                </>
            ),
            delivered: <ContentLoader mode="row" height={20} />,
            opened: <ContentLoader mode="row" height={20} />,
            clicked: <ContentLoader mode="row" height={20} />,
            spaceColumn: <ContentLoader mode="row" height={20} />,
            status: <ContentLoader mode="row" height={20} />,
            viewReport: <ContentLoader mode="row" height={20} />,
            rowID: index.toString(),
        });
    }
    const SkeletonTemplate = {
        tableTitle: <ContentLoader mode="row" />,
        audience: <ContentLoader mode="row" />,
        status: <ContentLoader mode="row" />,
        delivered: <ContentLoader mode="row" />,
        opened: <ContentLoader mode="row" />,
        clicked: <ContentLoader mode="row" />,
    };
    const SkeletonRows = map([...new Array(perPage)], (): MobileCardRowsProps => SkeletonTemplate);

    return isMobile ? SkeletonRows : rows;
};

export const useTableRows = (
    rowDropdownOptionsHandler: (
        ev: SyntheticEvent<HTMLElement>,
        option: { label: string; value: string },
        campaign: Campaign
    ) => void,
    onNameClick: (campaign: Campaign) => void
): CustomTableRowsProps[] => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const requestParams = useRequestParams();
    const isMobile = useMobile();
    const { campaigns } = useSelector((state: ApplicationState) => state);

    if (campaigns.loading) {
        return getSkeletonRows(requestParams.limit, isMobile);
    }

    const getViewReports = (singleCampaign): JSX.Element => (
        <div
            onClick={(ev): void => {
                ev.stopPropagation();

                const uri = (
                    singleCampaign.Type === CampaignType.AbSplit
                        ? CampaignsPaths.CampaignAbResults
                        : CampaignsPaths.CampaignOverview
                )
                    .replace(':type', singleCampaign.Type)
                    .replace(':name', safelyEncodeBase64Url(singleCampaign.Name));
                dispatch(Push(uri));
            }}
        >
            <Typography variant={'body1'} bodyVariant={'link'}>
                {t('viewreport')}
            </Typography>
        </div>
    );

    return map(
        campaigns?.collection,
        (singleCampaign: Campaign, index: number): CustomTableRowsProps | MobileCardRowsProps => {
            const campaignTemplatesThumbnail = singleCampaign?.CampaignTemplates[0]?.MediumScreenshot
                ? `${getApiPath()}/userfile${singleCampaign.CampaignTemplates[0].MediumScreenshot}`
                : '';

            let audienceArray = [];
            if (singleCampaign.Targets.ListNames && singleCampaign.Targets.SegmentNames) {
                audienceArray = singleCampaign.Targets.ListNames.concat(singleCampaign.Targets.SegmentNames);
            } else if (singleCampaign.Targets.ListNames) {
                audienceArray = singleCampaign.Targets.ListNames;
            } else if (singleCampaign.Targets.SegmentNames) {
                audienceArray = singleCampaign.Targets.SegmentNames;
            }

            const audience = audienceArray.join(', ');

            if (isMobile) {
                return {
                    tableTitle: singleCampaign.Name,
                    Audience: audience,
                    Status: getStatusBadge(singleCampaign.Status, index),
                    Delivered: <>{formatNumber(singleCampaign.Stats.Delivered)}</>,
                    Opened: <>{formatNumber(singleCampaign.Stats.Opened)}</>,
                    Clicked: <>{formatNumber(singleCampaign.Stats.Clicked)}</>,
                    returnOnItemClick: singleCampaign as any,
                    rowButtonText: t('viewreports'),
                };
            }

            return {
                preview: (
                    <div
                        onClick={(ev): void => {
                            ev.stopPropagation();
                        }}
                    >
                        <TemplatePreview
                            split={singleCampaign.CampaignTemplates.length > 1}
                            screenshot={campaignTemplatesThumbnail}
                            onClick={(): void => {
                                dispatch(PreviewCampaign(singleCampaign, false));
                            }}
                        />
                    </div>
                ),
                campaignName: (
                    <CampaignName campaign={singleCampaign} onNameClick={(): void => onNameClick(singleCampaign)} />
                ),
                delivered: singleCampaign.Stats.Delivered,
                opened: singleCampaign.Stats.Opened,
                clicked: singleCampaign.Stats.Clicked,
                spaceColumn: '',
                status: getStatusBadge(singleCampaign.Status, index),
                viewReport: (
                    <TableItemButtonsWrapper>
                        {getViewReports(singleCampaign)}
                        <div
                            onClick={(ev): void => {
                                ev.stopPropagation();
                            }}
                            style={{ cursor: 'pointer' }}
                        >
                            <Popover
                                type="icon"
                                options={getDropdownTableOptions(singleCampaign, false, t)}
                                enableCheckbox={false}
                                onItemClick={(ev, option: { label: string; value: string }): void => {
                                    rowDropdownOptionsHandler(ev, option, singleCampaign);
                                }}
                            >
                                <DotsIcon />
                            </Popover>
                        </div>
                    </TableItemButtonsWrapper>
                ),
                rowID: `${singleCampaign.Name}`,
            };
        }
    );
};
